import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDevice } from '../../context/DeviceContext';
import { WIRELESS_DEVICE_OFFLINE_LABEL } from '../../utils/DeviceCatalog';
import ChevronIcon from '../ChevronIcon';
import './ConnectDevice.css';

const CONNECTION_GUIDE_SEEN_KEY = 'mouse-driver.connectionGuideSeen';

const ConnectDevice = () => {
  const {
    isConnecting,
    error,
    pairedDevices,
    connectDevice,
    connectPairedDevice,
    retryPairedDeviceStatus,
    removePairedDevice,
  } = useDevice();
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [busyDeviceId, setBusyDeviceId] = useState(null);
  const [deletingDeviceId, setDeletingDeviceId] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const pairedListRef = useRef(null);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(CONNECTION_GUIDE_SEEN_KEY)) return;
      window.localStorage.setItem(CONNECTION_GUIDE_SEEN_KEY, '1');
    } catch {
      // 本地存储不可用时仍展示本次访问的首次指引。
    }
    setGuideStep(1);
    setShowGuide(true);
  }, []);

  const sortedPairedDevices = useMemo(
    () =>
      [...(pairedDevices || [])].sort(
        (a, b) => (b.lastConnectedAt || 0) - (a.lastConnectedAt || 0)
      ),
    [pairedDevices]
  );

  useEffect(() => {
    const list = pairedListRef.current;
    if (!list) return undefined;

    const updateScrollControls = () => {
      setCanScrollLeft(list.scrollLeft > 2);
      setCanScrollRight(
        list.scrollLeft + list.clientWidth < list.scrollWidth - 2
      );
    };

    updateScrollControls();
    list.addEventListener('scroll', updateScrollControls, { passive: true });
    const ResizeObserverConstructor = window.ResizeObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(updateScrollControls)
      : null;
    resizeObserver?.observe(list);

    return () => {
      list.removeEventListener('scroll', updateScrollControls);
      resizeObserver?.disconnect();
    };
  }, [sortedPairedDevices.length]);

  const handleAdd = async () => {
    setShowGuide(false);
    await connectDevice();
  };

  const handlePairedDevice = async (pairedDevice) => {
    const deviceId =
      pairedDevice.transportId ||
      `${pairedDevice.vendorId}:${pairedDevice.productId}`;
    setBusyDeviceId(deviceId);
    try {
      if (pairedDevice.isHistory) {
        // 浏览器已不再保留授权，只能重新弹出选择框；连上后按 MAC 认回这条记录
        await connectDevice();
      } else if (pairedDevice.accessible) {
        await connectPairedDevice(
          pairedDevice.transportId,
          pairedDevice.vendorId,
          pairedDevice.productId
        );
      } else if (pairedDevice.isReceiver) {
        await retryPairedDeviceStatus?.(
          pairedDevice.transportId,
          pairedDevice.vendorId,
          pairedDevice.productId
        );
      }
    } finally {
      setBusyDeviceId(null);
    }
  };

  const handleRemovePairedDevice = async (pairedDevice) => {
    const deviceId =
      pairedDevice.transportId ||
      `${pairedDevice.vendorId}:${pairedDevice.productId}`;
    const deviceName = pairedDevice.productName || '该设备';
    if (!window.confirm(`确定要删除“${deviceName}”吗？`)) return;

    setDeletingDeviceId(deviceId);
    try {
      await removePairedDevice(
        pairedDevice.transportId,
        pairedDevice.vendorId,
        pairedDevice.productId
      );
    } finally {
      setDeletingDeviceId(null);
    }
  };

  const scrollPairedDevices = (direction) => {
    const list = pairedListRef.current;
    if (!list) return;
    list.scrollBy({
      left: direction * Math.max(340, list.clientWidth * 0.78),
      behavior: 'smooth',
    });
  };

  return (
    <div className="connect-overlay">
      <header className="connect-brand">
        <span className="connect-brand-logo">WANI Hub</span>
      </header>

      <button
        type="button"
        className={`connect-button connect-button-guide connect-guide-trigger${
          showGuide ? ' is-guided' : ''
        }`}
        onClick={() => {
          setGuideStep(1);
          setShowGuide((visible) => !visible);
        }}
        disabled={isConnecting || busyDeviceId !== null}
      >
        连接指引
      </button>

      {sortedPairedDevices.length > 0 && (
        <section className="paired-device-picker" aria-label="已配对设备">
          <div className="paired-device-heading">
            <h1>选择设备</h1>
            <span>{sortedPairedDevices.length}</span>
          </div>
          <div className="paired-device-carousel">
            {canScrollLeft && (
              <button
                type="button"
                className="paired-device-scroll paired-device-scroll-left"
                aria-label="查看前面的设备"
                onClick={() => scrollPairedDevices(-1)}
              >
                <ChevronIcon direction="left" />
              </button>
            )}
            <div className="paired-device-list" ref={pairedListRef}>
              {sortedPairedDevices.map((pairedDevice) => {
                const deviceId =
                  pairedDevice.transportId ||
                  `${pairedDevice.vendorId}:${pairedDevice.productId}`;
                const isBusy = busyDeviceId === deviceId;
                const canRetry =
                  pairedDevice.isReceiver && !pairedDevice.accessible;
                const canSelect =
                  pairedDevice.accessible || canRetry || pairedDevice.isHistory;
                const statusLabel = pairedDevice.isHistory
                  ? pairedDevice.statusLabel
                  : pairedDevice.isReceiver && pairedDevice.statusKey === 'offline'
                    ? WIRELESS_DEVICE_OFFLINE_LABEL
                    : pairedDevice.isReceiver
                      ? pairedDevice.statusLabel || '检测中'
                      : pairedDevice.statusLabel || '已连接';
                const batteryLevel = Number(pairedDevice.batteryLevel);
                const hasBattery =
                  Number.isFinite(batteryLevel) &&
                  batteryLevel >= 0 &&
                  batteryLevel <= 100;
                const actionLabel = isBusy
                  ? pairedDevice.accessible || pairedDevice.isHistory
                    ? '连接中…'
                    : '检测中…'
                  : pairedDevice.isHistory
                    ? '重新授权'
                    : pairedDevice.accessible
                      ? '点击连接'
                      : canRetry
                        ? '重新检测'
                        : '暂不可用';

                return (
                  <div
                    className={`paired-device-card${
                      pairedDevice.accessible ? ' is-online' : ''
                    }`}
                    key={deviceId}
                  >
                    <button
                      type="button"
                      className="paired-device-delete"
                      aria-label={`删除${pairedDevice.productName || '已配对设备'}`}
                      title="删除已配对设备"
                      disabled={
                        isConnecting ||
                        busyDeviceId !== null ||
                        deletingDeviceId !== null
                      }
                      onClick={() => handleRemovePairedDevice(pairedDevice)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="paired-device-card-action"
                      disabled={
                        isConnecting ||
                        busyDeviceId !== null ||
                        deletingDeviceId !== null ||
                        !canSelect
                      }
                      onClick={() => handlePairedDevice(pairedDevice)}
                    >
                      <span className="paired-device-visual" aria-hidden="true">
                        {pairedDevice.image ? (
                          <img src={pairedDevice.image} alt="" />
                        ) : (
                          <i className="paired-device-mouse" />
                        )}
                      </span>
                      <span className="paired-device-copy">
                        <strong>{pairedDevice.productName || '已配对设备'}</strong>
                        <span className="paired-device-details">
                          <small>{isBusy ? '连接中…' : statusLabel}</small>
                          {hasBattery && (
                            <span
                              className={`paired-device-battery${
                                pairedDevice.charging ? ' is-charging' : ''
                              }`}
                              title={
                                pairedDevice.charging ? '充电中' : '设备电量'
                              }
                            >
                              <i className="paired-device-battery-icon">
                                <b style={{ width: `${batteryLevel}%` }} />
                              </i>
                              <em>{batteryLevel}%</em>
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="paired-device-card-footer">
                        <span className="paired-device-cta">
                          {deletingDeviceId === deviceId ? '删除中…' : actionLabel}
                          <span
                            className="paired-device-enter"
                            aria-hidden="true"
                          >
                            <ChevronIcon direction="right" />
                          </span>
                        </span>
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
            {canScrollRight && (
              <button
                type="button"
                className="paired-device-scroll paired-device-scroll-right"
                aria-label="查看更多设备"
                onClick={() => scrollPairedDevices(1)}
              >
                <ChevronIcon direction="right" />
              </button>
            )}
          </div>
        </section>
      )}

      {error && <div className="connect-inline-error">{error}</div>}

      <div className={`connect-actions${showGuide ? ' is-guided' : ''}`}>
        <div className={`connect-add-target${showGuide ? ' is-guided' : ''}`}>
          {showGuide && (
            <div
              className={`connect-guide-callout guide-callout-step-${guideStep}`}
              role="status"
            >
              <div className="guide-step-progress">{guideStep} / 3</div>

              {guideStep === 1 ? (
                <div className="guide-first-step">
                  <strong>点击「添加新设备」</strong>
                </div>
              ) : (
                <>
                  <div className="guide-picture-title">
                    {guideStep === 2 ? '在弹窗中选择一项' : '确认连接设备'}
                  </div>
                  <div className="guide-browser-picture" aria-hidden="true">
                    <div className="guide-browser-bar">
                      <strong>选择 HID 设备</strong>
                    </div>
                    <div className="guide-browser-origin">
                      <span className="guide-origin-icon">◇</span>
                      <span>此网站想要连接到一个 HID 设备</span>
                    </div>
                    <div className="guide-browser-content">
                      <div className="guide-browser-row is-selected">
                        <i className="guide-radio" />
                        <span className="guide-device-icon" />
                        <span className="guide-device-lines" />
                        {guideStep === 2 && (
                          <b className="guide-picture-badge badge-one">2</b>
                        )}
                      </div>
                      {guideStep === 2 && (
                        <>
                          <div className="guide-browser-row">
                            <i className="guide-radio" />
                            <span className="guide-device-icon" />
                            <span className="guide-device-lines" />
                          </div>
                          <div className="guide-browser-row">
                            <i className="guide-radio" />
                            <span className="guide-device-icon" />
                            <span className="guide-device-lines" />
                          </div>
                        </>
                      )}
                    </div>
                    {guideStep === 3 && (
                      <div className="guide-browser-footer">
                        <span className="guide-browser-cancel">取消</span>
                        <span className="guide-picture-arrow">→</span>
                        <span className="guide-browser-connect is-active">
                          连接
                        </span>
                        <b className="guide-picture-badge badge-two">3</b>
                      </div>
                    )}
                  </div>
                  <div className="guide-picture-caption">
                    {guideStep === 2 ? '选择列表中的任意一项' : '点击「连接」完成授权'}
                  </div>
                </>
              )}

              <div className="guide-step-actions">
                <button
                  type="button"
                  className="guide-next-button"
                  onClick={() => {
                    if (guideStep < 3) {
                      setGuideStep((step) => step + 1);
                    } else {
                      setShowGuide(false);
                    }
                  }}
                >
                  {guideStep < 3 ? '下一步' : '完成'}
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className="connect-button connect-button-primary"
            onClick={handleAdd}
            disabled={isConnecting || busyDeviceId !== null}
          >
            <span className="connect-button-icon" aria-hidden="true" />
            <span>{isConnecting ? '添加中…' : '添加新设备'}</span>
          </button>
        </div>
      </div>

      {showGuide && (
        <div
          className="connect-onboarding-mask"
          aria-hidden="true"
          onClick={() => setShowGuide(false)}
        />
      )}
    </div>
  );
};

export default ConnectDevice;
